import React from 'react';
import { AvJsonViewer, AvMenu, AvSnackBarMessage, AvStepper, AvStepperProps } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Typography } from '@mui/material';
import { useRouter } from '@tanstack/react-router';
import { setNestedValue } from '@utils/set-nested-value';
import { GraphQLError } from 'graphql';
import { useSnackbar } from 'notistack';
import { useAvMutation } from '../API/useAvQuery';
import { useAvContext } from '../context/AvContextProvider';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigate, useParams } from '../utils/AvRouter';
import { getGraphQLError } from '../utils/graphqlUtils';
import { isDebugMode } from '../utils/rum.utils';
import { formDisabledStyle } from '../utils/utils';

const getFieldErrorsArray = (graphQLError: GraphQLError) =>
  (graphQLError.extensions.message as string)?.split(', ')?.reduce((acc, error) => {
    const [field, message] = error.split(': ') as string[];
    return setNestedValue<Record<string, any>, string>(acc, field, message);
  }, {});

interface AvFormProps {
  title: React.ReactNode;
  name?: string;
  children: React.ReactNode;
  saveFunc: (onSuccess: any, onError?: any, v1?: any) => Promise<unknown> | void;
  setErrors?: (errors: any) => void;
  data?: Record<string, any>;
  disabled?: boolean;
  stepperProps?: Record<string, any> | null;
  limitWidth?: boolean;
  navigationOnSave?: string;
  sx?: Record<string, any>;
  padding?: string;
  menuOptions?: Array<Record<string, any>>;
  isAllowedEdit?: boolean;
  shouldDisableOpacityOnViewMode?: boolean;
  customGlobalActions?: React.ReactNode[];
  className?: string;
  isDirty?: boolean;
  footer?: React.ReactNode;
}

const AvForm: React.FC<AvFormProps> = ({
  title,
  name = '',
  children,
  saveFunc,
  data = {},
  setErrors = () => {},
  disabled = false,
  stepperProps,
  limitWidth = true,
  navigationOnSave,
  sx = {},
  padding = '24px 32px',
  menuOptions,
  isAllowedEdit = true,
  shouldDisableOpacityOnViewMode = false,
  customGlobalActions = [],
  className,
  isDirty = false,
  footer,
}) => {
  const { id } = useParams();
  const { getPathName } = useAvContext();
  const navigate = useNavigate();
  const router = useRouter();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleError = isTest => error => {
    const detailedError = () => {
      const failureInfo = errorsGraphQL?.failureInfo as any;
      return (
        <AvSnackBarMessage
          title={failureInfo?.category ? failureInfo.userMessage : 'Connection Validation Failed'}
          subTitle={failureInfo?.description || `The connection details provided were not validated by ${name}`}
          isTest={!!isTest}
          message={failureInfo?.resolutionSteps || error.message}
          closeSnackbar={() => closeSnackbar(snackBarKey)}
          onClickSkipValidation={save as any}
        />
      );
    };
    const errors = error.errors?.reduce(
      (map, { field, defaultMessage }) => ({ ...map, [`${isTest ? 'config.' : ''}${field}`]: defaultMessage }),
      {}
    );

    const graphQLError = getGraphQLError(error);
    const errorsGraphQL = (graphQLError || error)?.extensions?.failureInfo
      ? (graphQLError || error)?.extensions
      : graphQLError?.extensions?.code === 'FIELDS_VALIDATION_FAILED'
        ? getFieldErrorsArray(graphQLError)
        : undefined;

    const isValidationError = error?.status === 406 || errorsGraphQL?.status === 406;
    const snackBarKey =
      errorsGraphQL && !errorsGraphQL.failureInfo
        ? ''
        : enqueueSnackbar(
            isValidationError ? detailedError() : error.message || error,
            isValidationError ? ({ variant: 'static', persist: true, action: <span /> } as any) : { variant: 'error' }
          );

    setErrors(errors || errorsGraphQL || {});
  };

  const isDirectEntry = window.history.length === 1;
  const goBack = () => {
    if (!navigationOnSave && isDirectEntry) {
      navigate({ ...getPathName(''), ignoreBlocker: true });
    } else if (navigationOnSave) {
      navigate({ to: navigationOnSave, state: { id }, ignoreBlocker: true });
    } else {
      router.history.back();
      setTimeout(() => {
        navigate({ state: { id }, replace: true, ignoreBlocker: true });
      }, 0);
    }
  };

  const { mutateAsync: save, isPending: isSaving } = useAvMutation({
    mutationFn: saveParams => saveFunc(goBack, handleError(false), saveParams)!,
  });

  const { secondaryAction, isLoading, ...restStepperProps } = stepperProps || {};
  const defaultStepperProps: AvStepperProps = {
    steps: [{ name: 'Fill', nextText: 'Save' }],
    secondaryAction: secondaryAction?.func
      ? {
          label: '',
          ...secondaryAction,
          funcProps: { onSuccess: () => enqueueSnackbar('Test Passed', { variant: 'success' }), onError: handleError(true) },
        }
      : undefined,
    onNext: save,
    isLoading: isSaving || disabled || isLoading,
    ...restStepperProps,
  };

  useNavigationBlocker({ isDirty });
  return (
    <Box
      component="form"
      sx={{ ...flex.col, height: '100%', ...sx, '.MuiTextField-root, .MuiAutocomplete-root': { maxWidth: 450 } }}
      noValidate
      onSubmit={e => e.preventDefault()}
      autoComplete="off"
      className={className}>
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          padding,
          '.MuiCollapse-root': isAllowedEdit || shouldDisableOpacityOnViewMode ? {} : formDisabledStyle,
        }}>
        <Box sx={{ ...flex.col, height: '100%', maxWidth: limitWidth ? '1200px' : 'unset', mx: 'auto', '> div': { flexShrink: 0 } }}>
          <Box sx={{ ...flex.justifyBetweenCenter, gap: 2, mb: title ? 2 : undefined }}>
            {title && (
              <Typography variant="h3" sx={{ ...flex.itemsCenter, gap: 2, whiteSpace: 'pre', margin: 0 }}>
                {title}
              </Typography>
            )}
            <Box sx={{ ...flex.row, gap: '8px' }}>
              {customGlobalActions?.map((action, index) => (
                <React.Fragment key={`action-${index + 1}`}>{action}</React.Fragment>
              ))}{' '}
              {menuOptions ? <AvMenu options={menuOptions} /> : null}
            </Box>
          </Box>
          {children}
          {isDebugMode() ? <AvJsonViewer data={data} /> : null}
          {isAllowedEdit && <hr style={{ marginTop: 24, borderColor: 'transparent' }} />}
        </Box>
      </Box>
      {footer}
      {(isAllowedEdit || stepperProps?.showIfReadOnly) && <AvStepper onCancel={goBack} {...defaultStepperProps} />}
    </Box>
  );
};

export default AvForm;
